import {
    Button as AriaButton,
    Dialog as AriaDialog,
    DialogTrigger as AriaDialogTrigger,
    Popover as AriaPopover,
} from "react-aria-components";
import { AlertCircle, ArrowRight, ChevronDown } from "@untitledui/icons";
import { Button } from "@tricura/atlas-ui";

interface LocationScheduleWarningProps {
    /** The Loss Run row's location value that has no match in the
     *  submission's policy Schedule. Shown in the trigger + bold in the
     *  popover. */
    location: string;
    /** Called when the CTA is clicked — typically navigates to the
     *  submission's Schedule section. */
    onCheckSchedule?: () => void;
}

/**
 * Loss Runs "Location" cell for a value that isn't in the policy
 * Schedule (the same lookup that drives SIR by Community — see
 * `loss-run-derive.ts`). Per the Figma (link #3) + Andressa's review:
 * it renders as a dropdown-styled control with a leading round warning
 * glyph (`AlertCircle`), and clicking it opens the message + a CTA back
 * to the Schedule page. Plain in-flow element (no absolute/z-index), so
 * it scrolls and clips like every other cell on horizontal scroll.
 */
export function LocationScheduleWarning({ location, onCheckSchedule }: LocationScheduleWarningProps) {
    return (
        <AriaDialogTrigger>
            <AriaButton
                aria-label={`Location ${location} is not in the policy Schedule`}
                className="bg-primary shadow-xs ring-primary hover:bg-secondary_hover focus-visible:ring-brand aria-expanded:ring-brand inline-flex w-full cursor-pointer items-center gap-2 rounded-lg px-3 py-2 text-left ring-1 outline-none transition duration-100 ease-linear ring-inset focus-visible:ring-2 aria-expanded:ring-2"
            >
                <AlertCircle aria-hidden="true" className="text-utility-yellow-500 size-4 shrink-0" />
                <span className="text-primary truncate text-sm font-medium">{location || "—"}</span>
                <ChevronDown aria-hidden="true" className="text-fg-quaternary ml-auto size-4 shrink-0 stroke-[2.25px]" />
            </AriaButton>
            <AriaPopover
                placement="bottom start"
                offset={6}
                className="z-50 entering:animate-in entering:fade-in entering:zoom-in-95"
            >
                <AriaDialog className="bg-primary border-secondary w-[300px] min-w-[256px] rounded-lg border p-4 shadow-lg outline-none">
                    <p className="text-secondary text-sm">
                        Location <span className="text-primary font-semibold">{location}</span> is not in the policy
                        Schedule.
                    </p>
                    {/* Full-bleed divider — `-mx-4` cancels the parent's
                      * `p-4` so the line spans the popover edges. */}
                    <hr aria-hidden="true" className="border-secondary -mx-4 my-4" />
                    <Button color="primary" size="sm" iconTrailing={ArrowRight} onClick={onCheckSchedule}>
                        Check Schedule Page
                    </Button>
                </AriaDialog>
            </AriaPopover>
        </AriaDialogTrigger>
    );
}
